import { useOutletContext } from 'react-router-dom';
import { BanknotesIcon, ExclamationTriangleIcon } from '@heroicons/react/24/solid';

import AnalyticsTable from '../../../components/AiSpend/AnalyticsTable';
import ErrorState from '../../../components/DataDisplay/ErrorState';
import StatCard from '../../../components/DataDisplay/StatCard';
import { getAiSpendErrors } from '../../../services/aiSpendService';
import {
  formatCount,
  formatInstant,
  formatMoney,
  formatPercent,
} from '../../../utils/helpers/aiSpendFormat';
import useApiResource from '../../../utils/hooks/useApiResource';

export default function AiSpendErrors() {
  const { queryParams, queryKey, options } = useOutletContext();
  const { data, error, loading, refetch } = useApiResource(
    ({ signal }) => getAiSpendErrors({ params: queryParams, signal }),
    { deps: [queryKey] },
  );

  if (error) return <ErrorState message={error} onRetry={refetch} />;
  const totals = data?.totals || {};

  return (
    <div className="space-y-6">
      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          icon={ExclamationTriangleIcon}
          tone="red"
          label="Failed calls"
          value={formatCount(totals.failed_calls)}
          rawValue={totals.failed_calls}
          detail={
            totals.success_rate != null
              ? `${formatPercent(1 - totals.success_rate)} of all calls`
              : ''
          }
          loading={loading}
        />
        <StatCard
          icon={BanknotesIcon}
          tone="amber"
          label="Wasted spend"
          value={formatMoney(data?.wasted_cost_usd)}
          rawValue={data?.wasted_cost_usd}
          detail="Paid for, returned nothing"
          loading={loading}
        />
        <StatCard
          tone="amber"
          label="Billed but unusable"
          value={formatMoney(data?.unusable_cost_usd)}
          rawValue={data?.unusable_cost_usd}
          detail={`${formatCount(data?.unusable_calls)} calls the provider completed and the feature could not use`}
          loading={loading}
        />
        <StatCard
          tone="slate"
          label="Retried operations"
          value={formatCount(totals.retried_call_groups)}
          rawValue={totals.retried_call_groups}
          detail="Logical calls that cost more than once"
          loading={loading}
        />
      </section>

      <section>
        <h2 className="mb-2 text-lg font-semibold text-slate-900 dark:text-white">
          By error
        </h2>
        <AnalyticsTable
          loading={loading}
          rows={data?.by_error_class || []}
          rowKey={(row) => row.error_class}
          emptyTitle="No failures in this period"
          emptyMessage="Every recorded call completed."
          columns={[
            { key: 'error_class', label: 'Error', render: (row) => row.error_class },
            { key: 'calls', label: 'Calls', render: (row) => formatCount(row.calls) },
            {
              key: 'cost_usd',
              label: 'Cost',
              render: (row) => formatMoney(row.cost_usd),
            },
            {
              key: 'last_seen',
              label: 'Last seen',
              render: (row) => formatInstant(row.last_seen, options?.timezone),
            },
          ]}
        />
      </section>

      <div className="grid gap-6 lg:grid-cols-2">
        <section>
          <h2 className="mb-2 text-lg font-semibold text-slate-900 dark:text-white">
            By feature
          </h2>
          <AnalyticsTable
            loading={loading}
            rows={data?.by_feature || []}
            columns={[
              { key: 'label', label: 'Feature', render: (row) => row.label },
              { key: 'calls', label: 'Failures', render: (row) => formatCount(row.calls) },
              { key: 'cost_usd', label: 'Cost', render: (row) => formatMoney(row.cost_usd) },
            ]}
          />
        </section>
        <section>
          <h2 className="mb-2 text-lg font-semibold text-slate-900 dark:text-white">
            By HTTP status
          </h2>
          <AnalyticsTable
            loading={loading}
            rows={data?.by_http_status || []}
            rowKey={(row) => row.http_status}
            emptyTitle="No HTTP-level failures"
            emptyMessage="Failures here came back as exceptions, not status codes."
            columns={[
              { key: 'http_status', label: 'Status', render: (row) => row.http_status },
              { key: 'calls', label: 'Calls', render: (row) => formatCount(row.calls) },
            ]}
          />
        </section>
      </div>
    </div>
  );
}
